/**
 * RecordSelectionBar
 *
 * Header row above the list: a select-all checkbox plus, once rows are ticked,
 * a single "Actions" dropdown (set status, add/remove groups, archive, delete,
 * clear). "Select All" escalates the operation to the whole active
 * filter — the server resolves it via Algolia rather than receiving every id.
 * Status/archive/delete confirm via a declarative <Modal> (rendered in-tree so
 * it inherits the app theme); group add/remove uses a modal picker that doubles
 * as that confirmation. Each action refreshes the list and clears the selection.
 */
import { FC, useContext, useState } from 'react';
import { Button, Checkbox, Dropdown, Modal, Typography, theme } from 'antd';
import type { MenuProps } from 'antd';
import { useSnapshot } from 'valtio';

import { RecordStatus } from '#types/System.Parameters.types';
import type { BulkRecordAction, BulkRecordTarget } from '#types';
import { MessageContext } from '@/providers/MessageContext';
import FlexBox from '../atoms/FlexBox';
import { EntityGroupMultiSelector } from '../artifacts/EntityGroupSelector';
import { bulkUpdateRecords } from './bulkUpdateRecords';
import {
    recordsBrowserState,
    resetRecordSelection,
} from './recordsBrowserState';
import Icon from '../atoms/Icon';

const { Text } = Typography;

interface RecordSelectionBarProps {
    entityId: string;
    statusFilters: RecordStatus[];
    groupIds: string[];
    query: string;
    onAfterBulk: () => void;
    onSelectAll: () => void;
}

const RecordSelectionBar: FC<RecordSelectionBarProps> = ({
    entityId,
    statusFilters,
    groupIds,
    query,
    onAfterBulk,
    onSelectAll,
}) => {
    const { token } = theme.useToken();
    const { messageApi } = useContext(MessageContext);
    const snap = useSnapshot(recordsBrowserState);

    const [groupMode, setGroupMode] = useState<'add' | 'remove' | null>(null);
    const [groupPicks, setGroupPicks] = useState<string[]>([]);
    const [confirm, setConfirm] = useState<{ action: BulkRecordAction; label: string; danger: boolean } | null>(null);

    const selectAllMatching = snap.selectAllMatching;
    const hasExplicitSelection = snap.selection.selectedRowIds.length > 0;
    const hasSelection = hasExplicitSelection || selectAllMatching;
    const affectedText = selectAllMatching
        ? 'This will affect all records matching the current filter.'
        : 'This will affect the selected records.';

    const toggleSelectAll = () => {
        if (hasSelection) resetRecordSelection();
        else onSelectAll();
    };

    const buildTarget = (): BulkRecordTarget =>
        selectAllMatching
            ? {
                filter: {
                    entityGroupIds: groupIds.length ? groupIds : undefined,
                    statuses: statusFilters.length ? statusFilters : undefined,
                    query: query.trim() || undefined,
                },
            }
            : { ids: [...recordsBrowserState.selection.selectedRowIds] };

    const executeBulk = async (action: BulkRecordAction, label: string) => {
        try {
            await bulkUpdateRecords({ entityId, target: buildTarget(), action });
            messageApi.success(`${label} complete`);
            onAfterBulk();
        } catch {
            messageApi.error(`${label} failed`);
        }
    };

    const confirmBulk = (action: BulkRecordAction, label: string, danger = false) => {
        setConfirm({ action, label, danger });
    };

    const runConfirm = () => {
        if (!confirm) return;
        return executeBulk(confirm.action, confirm.label).then(() => setConfirm(null));
    };

    const actionItems: MenuProps['items'] = [
        {
            key: 'status',
            label: 'Set status',
            children: Object.values(RecordStatus).map((status) => ({ key: `status:${status}`, label: status })),
        },
        { type: 'divider' },
        { key: 'addGroups', label: 'Add to groups' },
        { key: 'removeGroups', label: 'Remove from groups' },
        { type: 'divider' },
        { key: 'archive', label: 'Archive' },
        { key: 'delete', label: 'Delete', danger: true },
        { type: 'divider' },
        { key: 'clear', label: 'Clear selection' },
    ];

    const openGroupModal = (mode: 'add' | 'remove') => {
        setGroupPicks([]);
        setGroupMode(mode);
    };

    const onActionClick: MenuProps['onClick'] = ({ key }) => {
        if (key.startsWith('status:')) {
            const status = key.slice('status:'.length) as RecordStatus;
            confirmBulk({ type: 'setStatus', status }, `Set status to ${status}`);
            return;
        }
        switch (key) {
            case 'addGroups':
                openGroupModal('add');
                break;
            case 'removeGroups':
                openGroupModal('remove');
                break;
            case 'archive':
                confirmBulk({ type: 'archive' }, 'Archive');
                break;
            case 'delete':
                confirmBulk({ type: 'delete' }, 'Delete', true);
                break;
            case 'clear':
                resetRecordSelection();
                break;
        }
    };

    const groupLabel = groupMode === 'remove' ? 'Remove from groups' : 'Add to groups';
    const confirmGroupBulk = () => {
        const action: BulkRecordAction =
            groupMode === 'remove'
                ? { type: 'removeGroups', groupIds: groupPicks }
                : { type: 'addGroups', groupIds: groupPicks };
        void executeBulk(action, groupLabel);
        setGroupMode(null);
        setGroupPicks([]);
    };

    return (
        <FlexBox
            column
            noGrow
            gap={5}
            style={{ 
                width: '100%', 
                paddingLeft: 23,
                paddingRight: 12,
                paddingBottom: 10,
                boxSizing: 'border-box', 
                borderBottom: `1px solid ${token.colorBorderSecondary}` 
            }}
        >
            <FlexBox noGrow gap={10} style={{ width: '100%' }}>
                <Checkbox
                    checked={hasSelection}
                    onChange={toggleSelectAll}
                    aria-label="select-all-records"
                />
                {selectAllMatching ? (
                    <Text style={{ whiteSpace: 'nowrap' }}>All selected</Text>
                ) : hasExplicitSelection ? (
                    <Button
                        type="link"
                        size="small"
                        style={{ paddingInline: 0, whiteSpace: 'nowrap' }}
                        onClick={() => { recordsBrowserState.selectAllMatching = true; }}
                    >
                        Select All
                    </Button>
                ) : null}
                <Dropdown
                    menu={{ items: actionItems, onClick: onActionClick }}
                    trigger={['click']}
                    disabled={!hasSelection}
                >
                    <Button 
                        type="link"
                        size="small" 
                        disabled={!hasSelection} 
                        aria-label="Record actions"
                    >
                        Actions <Icon.ChevronDown />
                    </Button>
                </Dropdown>
            </FlexBox>

            <Modal
                open={confirm !== null}
                title={confirm ? `${confirm.label}?` : ''}
                okText={confirm?.label}
                okButtonProps={{ danger: confirm?.danger }}
                onOk={runConfirm}
                onCancel={() => setConfirm(null)}
            >
                <Text>{affectedText}</Text>
            </Modal>

            <Modal
                open={groupMode !== null}
                title={groupLabel}
                okText={groupMode === 'remove' ? 'Remove' : 'Add'}
                okButtonProps={{ disabled: groupPicks.length === 0 }}
                onOk={confirmGroupBulk}
                onCancel={() => setGroupMode(null)}
                destroyOnHidden
            >
                <FlexBox column gap={5} noGrow alignStart style={{ width: '100%' }}>
                    <Text type="secondary">{affectedText}</Text>
                    <EntityGroupMultiSelector
                        entityId={entityId}
                        value={groupPicks.map((entityGroupId) => ({ entityId, entityGroupId }))}
                        onChange={(selections) =>
                            setGroupPicks(selections ? selections.map((s) => s.entityGroupId).filter((id): id is string => Boolean(id)) : [])
                        }
                        style={{ width: '100%' }}
                    />
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                        {groupPicks.length} group{groupPicks.length === 1 ? '' : 's'} selected
                    </Text>
                </FlexBox>
            </Modal>
        </FlexBox>
    );
};

export default RecordSelectionBar;
